import { cn } from '@pm/ui';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ApiRequestError } from './api';

type Tone = 'success' | 'danger' | 'info';
interface Toast {
  id: number;
  tone: Tone;
  title: string;
  body?: string;
  code?: string;
}

interface ToastApi {
  push: (t: Omit<Toast, 'id'>) => void;
  success: (title: string, body?: string) => void;
  error: (err: unknown, fallback?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** Toasts em vidro no canto inferior direito, com código de erro em mono quando houver (skill → components.md §Toasts). */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback(
    (id: number) => setItems((list) => list.filter((t) => t.id !== id)),
    [],
  );

  const push = useCallback(
    (t: Omit<Toast, 'id'>) => {
      const id = ++seq.current;
      setItems((list) => [...list.slice(-3), { ...t, id }]);
      window.setTimeout(() => dismiss(id), 5000);
    },
    [dismiss],
  );

  const value = useMemo<ToastApi>(
    () => ({
      push,
      success: (title, body) => push({ tone: 'success', title, body }),
      error: (err, fallback = 'Algo deu errado.') => {
        if (err instanceof ApiRequestError)
          push({
            tone: 'danger',
            title: err.message,
            code: err.code,
            body: detailsText(err.details),
          });
        else
          push({
            tone: 'danger',
            title: fallback,
            body: err instanceof Error ? err.message : undefined,
          });
      },
    }),
    [push],
  );

  const icons = { success: CheckCircle2, danger: AlertTriangle, info: Info };
  const colors = { success: 'var(--success)', danger: 'var(--danger)', info: 'var(--info)' };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 right-4 z-(--z-toast) flex w-[360px] max-w-[calc(100vw-32px)] flex-col gap-2"
      >
        {items.map((t) => {
          const Icon = icons[t.tone];
          return (
            <div
              key={t.id}
              role="status"
              style={{ borderLeftColor: colors[t.tone] }}
              className={cn(
                'glass pointer-events-auto flex gap-2.5 rounded-md border-l-2 px-3 py-2.5 shadow-popover',
              )}
            >
              <Icon
                className="mt-0.5 size-4 shrink-0"
                style={{ color: colors[t.tone] }}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <p className="font-medium leading-tight">{t.title}</p>
                {t.body ? <p className="mt-0.5 text-sm text-text-2">{t.body}</p> : null}
                {t.code ? <p className="mt-1 font-mono text-xs text-text-3">{t.code}</p> : null}
              </div>
              <button
                type="button"
                aria-label="Fechar"
                onClick={() => dismiss(t.id)}
                className="text-text-3 hover:text-text-1"
              >
                <X className="size-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast precisa de <ToastProvider>');
  return ctx;
}

function detailsText(details?: Record<string, unknown>): string | undefined {
  if (!details) return undefined;
  const issues = details.issues as { path: string; message: string }[] | undefined;
  if (issues?.length) return issues.map((i) => `${i.path}: ${i.message}`).join(' · ');
  const blocked = details.blocked_by as { number: number; title: string }[] | undefined;
  if (blocked?.length)
    return `Aguardando: ${blocked.map((b) => `#${b.number} ${b.title}`).join(', ')}`;
  return undefined;
}
